/**
 * Lesson plans: stage templates, minute budgets that add up, and the finished plan as
 * plain text and as HTML for pasting into a document.
 */

export type LessonTemplate = "simple" | "five-e" | "hunter";

export interface StageSpec {
  /** Key into the widget's stage names and prompts. */
  id: string;
  /** Default share of the lesson, in percent. A starting point, not a rule. */
  weight: number;
}

/**
 * Stage order and default time shares.
 *
 * 5E: the BSCS instructional model (Engage, Explore, Explain, Elaborate, Evaluate).
 * Hunter: the lesson-design elements Madeline Hunter set out in *Mastery Teaching*
 * (1982), in the order most planners use them.
 */
export const LESSON_TEMPLATES: Record<LessonTemplate, StageSpec[]> = {
  simple: [
    { id: "introduction", weight: 15 },
    { id: "instruction", weight: 30 },
    { id: "practice", weight: 40 },
    { id: "closure", weight: 15 },
  ],
  "five-e": [
    { id: "engage", weight: 10 },
    { id: "explore", weight: 25 },
    { id: "explain", weight: 20 },
    { id: "elaborate", weight: 25 },
    { id: "evaluate", weight: 20 },
  ],
  hunter: [
    { id: "anticipatory-set", weight: 5 },
    { id: "objective", weight: 5 },
    { id: "input", weight: 15 },
    { id: "modeling", weight: 15 },
    { id: "check", weight: 10 },
    { id: "guided-practice", weight: 20 },
    { id: "closure", weight: 5 },
    { id: "independent-practice", weight: 25 },
  ],
};

/**
 * Whole minutes in proportion to `weights` that add up to exactly `total`
 * (largest-remainder rounding). Every stage keeps at least a minute when the total
 * allows it.
 */
export function allocateMinutes(
  total: number,
  weights: readonly number[]
): number[] {
  const count = weights.length;
  if (count === 0 || total <= 0) {
    return weights.map(() => 0);
  }
  const positive = weights.map((weight) => Math.max(0, weight));
  const sum = positive.reduce((acc, weight) => acc + weight, 0);
  const shares =
    sum > 0
      ? positive.map((weight) => (weight * total) / sum)
      : positive.map(() => total / count);
  const minutes = shares.map(Math.floor);
  let left = total - minutes.reduce((acc, value) => acc + value, 0);
  const byRemainder = shares
    .map((share, index) => ({ index, remainder: share - Math.floor(share) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of byRemainder) {
    if (left <= 0) {
      break;
    }
    minutes[index] = (minutes[index] ?? 0) + 1;
    left -= 1;
  }
  if (total >= count) {
    for (const [index, value] of minutes.entries()) {
      if (value > 0) {
        continue;
      }
      const largest = minutes.indexOf(Math.max(...minutes));
      minutes[largest] = (minutes[largest] ?? 0) - 1;
      minutes[index] = 1;
    }
  }
  return minutes;
}

export interface LessonStage {
  name: string;
  minutes: number;
  notes: string;
}

export interface LessonPlan {
  title: string;
  subject: string;
  grade: string;
  duration: number;
  objectives: string[];
  standards: string[];
  materials: string[];
  stages: LessonStage[];
  assessment: string;
  differentiation: string;
  reflection: string;
}

export interface LessonPlanLabels {
  untitled: string;
  subject: string;
  grade: string;
  duration: string;
  objectives: string;
  standards: string;
  materials: string;
  procedure: string;
  stage: string;
  time: string;
  activities: string;
  total: string;
  assessment: string;
  differentiation: string;
  reflection: string;
  /** "45 minutes", in the reader's language. */
  minutes: (count: number) => string;
}

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};
const HTML_SPECIAL = /[&<>"]/g;
const LINE_BREAK = /\r?\n/;

function escapeHtml(text: string): string {
  return text.replace(HTML_SPECIAL, (char) => HTML_ESCAPES[char] ?? char);
}

function nonEmpty(text: string): boolean {
  return text.trim().length > 0;
}

function headerParts(plan: LessonPlan, labels: LessonPlanLabels): string[] {
  return [
    nonEmpty(plan.subject) ? `${labels.subject}: ${plan.subject.trim()}` : "",
    nonEmpty(plan.grade) ? `${labels.grade}: ${plan.grade.trim()}` : "",
    plan.duration > 0
      ? `${labels.duration}: ${labels.minutes(plan.duration)}`
      : "",
  ].filter(nonEmpty);
}

function textList(heading: string, items: readonly string[]): string[] {
  const kept = items.filter(nonEmpty);
  return kept.length === 0
    ? []
    : [heading, ...kept.map((item) => `- ${item.trim()}`), ""];
}

function textBlock(heading: string, body: string): string[] {
  return nonEmpty(body) ? [heading, body.trim(), ""] : [];
}

/** The plan as plain text — for the clipboard, a .txt download, or an email. */
export function lessonPlanText(
  plan: LessonPlan,
  labels: LessonPlanLabels
): string {
  const total = plan.stages.reduce((acc, stage) => acc + stage.minutes, 0);
  const lines = [
    plan.title.trim() || labels.untitled,
    headerParts(plan, labels).join(" · "),
    "",
    ...textList(labels.objectives, plan.objectives),
    ...textList(labels.standards, plan.standards),
    ...textList(labels.materials, plan.materials),
    labels.procedure,
    ...plan.stages.flatMap((stage, index) => [
      `${index + 1}. ${stage.name} (${labels.minutes(stage.minutes)})`,
      ...stage.notes
        .split(LINE_BREAK)
        .filter(nonEmpty)
        .map((note) => `   ${note.trim()}`),
    ]),
    `${labels.total}: ${labels.minutes(total)}`,
    "",
    ...textBlock(labels.assessment, plan.assessment),
    ...textBlock(labels.differentiation, plan.differentiation),
    ...textBlock(labels.reflection, plan.reflection),
  ];
  return lines.join("\n").trim();
}

function htmlList(heading: string, items: readonly string[]): string {
  const kept = items.filter(nonEmpty);
  if (kept.length === 0) {
    return "";
  }
  const listItems = kept
    .map((item) => `<li>${escapeHtml(item.trim())}</li>`)
    .join("");
  return `<h2>${escapeHtml(heading)}</h2><ul>${listItems}</ul>`;
}

function htmlBlock(heading: string, body: string): string {
  if (!nonEmpty(body)) {
    return "";
  }
  const paragraphs = body
    .split(LINE_BREAK)
    .filter(nonEmpty)
    .map((line) => `<p>${escapeHtml(line.trim())}</p>`)
    .join("");
  return `<h2>${escapeHtml(heading)}</h2>${paragraphs}`;
}

/** The plan as HTML, so headings, lists and the procedure table survive a paste. */
export function lessonPlanHtml(
  plan: LessonPlan,
  labels: LessonPlanLabels
): string {
  const total = plan.stages.reduce((acc, stage) => acc + stage.minutes, 0);
  const rows = plan.stages
    .map(
      (stage) =>
        `<tr><td>${escapeHtml(stage.name)}</td><td>${escapeHtml(labels.minutes(stage.minutes))}</td><td>${stage.notes
          .split(LINE_BREAK)
          .filter(nonEmpty)
          .map((line) => escapeHtml(line.trim()))
          .join("<br>")}</td></tr>`
    )
    .join("");
  return [
    `<h1>${escapeHtml(plan.title.trim() || labels.untitled)}</h1>`,
    `<p>${escapeHtml(headerParts(plan, labels).join(" · "))}</p>`,
    htmlList(labels.objectives, plan.objectives),
    htmlList(labels.standards, plan.standards),
    htmlList(labels.materials, plan.materials),
    `<h2>${escapeHtml(labels.procedure)}</h2>`,
    `<table border="1" cellpadding="6" style="border-collapse:collapse"><thead><tr><th>${escapeHtml(labels.stage)}</th><th>${escapeHtml(labels.time)}</th><th>${escapeHtml(labels.activities)}</th></tr></thead><tbody>${rows}</tbody></table>`,
    `<p><strong>${escapeHtml(labels.total)}:</strong> ${escapeHtml(labels.minutes(total))}</p>`,
    htmlBlock(labels.assessment, plan.assessment),
    htmlBlock(labels.differentiation, plan.differentiation),
    htmlBlock(labels.reflection, plan.reflection),
  ].join("");
}
